import type { TelegramCloudStorage } from "../telegram-types.js";

import { CloudStorageUnavailableError } from "./types.js";

/**
 * Promise wrapper around Telegram WebApp's CloudStorage callback API.
 *
 * The official surface is `wa.CloudStorage.{set,get,getItems,remove,
 * removeItems,getKeys}Item(s)?(...args, callback)`. We adapt to Promises
 * so consumers can use async/await.
 *
 * Construction is deferred — pass the underlying `wa.CloudStorage` (or
 * `null` outside Telegram). All methods reject with
 * `CloudStorageUnavailableError` when no underlying API is available.
 */
export class CloudStorageApi {
  constructor(private readonly impl: TelegramCloudStorage | null) {}

  available(): boolean {
    return this.impl !== null;
  }

  async setItem(key: string, value: string): Promise<void> {
    const cs = this.required();
    return new Promise((resolve, reject) => {
      cs.setItem(key, value, (err) => {
        if (err) reject(err);
        else resolve();
      });
    });
  }

  async getItem(key: string): Promise<string | null> {
    const cs = this.required();
    return new Promise((resolve, reject) => {
      cs.getItem(key, (err, value) => {
        if (err) reject(err);
        else resolve(value && value.length > 0 ? value : null);
      });
    });
  }

  async removeItem(key: string): Promise<void> {
    const cs = this.required();
    return new Promise((resolve, reject) => {
      cs.removeItem(key, (err) => {
        if (err) reject(err);
        else resolve();
      });
    });
  }

  async removeItems(keys: string[]): Promise<void> {
    if (keys.length === 0) return;
    const cs = this.required();
    return new Promise((resolve, reject) => {
      cs.removeItems(keys, (err) => {
        if (err) reject(err);
        else resolve();
      });
    });
  }

  async getKeys(): Promise<string[]> {
    const cs = this.required();
    return new Promise((resolve, reject) => {
      cs.getKeys((err, keys) => {
        if (err) reject(err);
        else resolve(keys ?? []);
      });
    });
  }

  private required(): TelegramCloudStorage {
    if (!this.impl) throw new CloudStorageUnavailableError();
    return this.impl;
  }
}
